'use client';

import { c as _c } from "react/compiler-runtime";
import { useState, useRef, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import Link from 'next/link';
import Image from 'next/image';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHiveIconSmallLight } from '@awesome.me/kit-94e593bf23/icons/kit/custom';
import Price from '@/app/_components/Price';
import { useHelpWidget } from '../HelpWidgetContext';
import { placeholder as inputPlaceholder } from '@/app/_lib/config/shopping-assistant-config';

// Tool results carry full URLs so Claudio can write them into a sentence a
// customer can click. next/link wants the path: an absolute URL navigates
// off to that origin, which on dev is a different port than the one the
// widget is running on.
const SITE_ORIGIN = (process.env.NEXT_PUBLIC_SITE_URL || '').replace(/\/+$/, '');
const isOffSite = url => {
  if (!/^https?:\/\//i.test(url) || !SITE_ORIGIN) return false;
  try {
    return new URL(url).origin !== new URL(SITE_ORIGIN).origin;
  } catch (e) {
    return false;
  }
};
const toPath = url => {
  if (!url) return '#';
  if (!/^https?:\/\//i.test(url)) return url;
  try {
    const parsed = new URL(url);
    return `${parsed.pathname}${parsed.search}${parsed.hash}`;
  } catch (e) {
    return url;
  }
};

// Claudio writes full URLs and info@hivemodern.com into its sentences.
// Rendered as plain text they are something to copy and paste; nobody does
// that. Split the text on them and make each one a link in the message.
const URL_OR_EMAIL_IN_TEXT = /(https?:\/\/[^\s<>"')\]]+|[A-Za-z0-9._%+-]+@[A-Za-z0-9-]+(?:\.[A-Za-z0-9-]+)+)/g;
// Same color as the surrounding message text; the underline carries it
const LINK_CLASS = 'underline break-words';
const linkify = text => {
  if (!text) return text;
  return text.split(URL_OR_EMAIL_IN_TEXT).map((part, i) => {
    if (i % 2 === 0) return part;
    // A link at the end of a sentence picks up its punctuation
    const trailing = part.match(/[.,;:!?)\]]+$/);
    const target = trailing ? part.slice(0, -trailing[0].length) : part;
    return <span key={i}>
                {target.includes('@') ? <a href={`mailto:${target}`} className={LINK_CLASS}>{target}</a> : isOffSite(target) ? <a href={target} className={LINK_CLASS} target="_blank" rel="noopener noreferrer">{target}</a> : <Link href={toPath(target)} className={LINK_CLASS}>{target}</Link>}
                {trailing ? trailing[0] : null}
            </span>;
  });
};
const HelpAIClaudio = () => {
  const {
    aiMessages,
    setAiMessages,
    aiSessionId,
    setAiSessionId,
    currentPanel,
    clearAiConversation,
    minimizeWidget
  } = useHelpWidget();

  // Initialize session ID if not already set
  useEffect(() => {
    if (!aiSessionId) {
      setAiSessionId(crypto.randomUUID());
    }
  }, [aiSessionId, setAiSessionId]);
  const messages = aiMessages;
  const setMessages = setAiMessages;
  const sessionId = aiSessionId || '';
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Extract products and variants from messages for side panel - newest first, in order of appearance
  // Note: designers and brands are rendered inline in the message, not in the panel
  const panelItems = useMemo(() => {
    const items = [];
    const seenProductIds = new Set();
    const seenVariantIds = new Set();
    const productRegex = /```product\s*\n?([\s\S]*?)```/g;
    const variantRegex = /```variant\s*\n?([\s\S]*?)```/g;

    // Iterate messages in reverse to get newest items first
    const reversedMessages = [...messages].reverse();
    reversedMessages.forEach(message => {
      if (message.role === 'assistant') {
        // Find all matches with their positions to maintain order within message
        const matches = [];
        let match;
        while ((match = productRegex.exec(message.content)) !== null) {
          try {
            const data = JSON.parse(match[1].trim());
            if (!seenProductIds.has(data.id)) {
              seenProductIds.add(data.id);
              matches.push({
                type: 'product',
                data,
                index: match.index
              });
            }
          } catch (e) {/* skip */}
        }
        while ((match = variantRegex.exec(message.content)) !== null) {
          try {
            const data_0 = JSON.parse(match[1].trim());
            if (!seenVariantIds.has(data_0.variant_id)) {
              seenVariantIds.add(data_0.variant_id);
              matches.push({
                type: 'variant',
                data: data_0,
                index: match.index
              });
            }
          } catch (e_0) {/* skip */}
        }

        // Sort by position within message, then add to items
        matches.sort((a, b) => a.index - b.index);
        items.push(...matches);
      }
    });
    return items;
  }, [messages]);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({
      behavior: 'smooth'
    });
  }, [messages]);

  // Focus input on mount
  useEffect(() => {
    inputRef.current?.focus();
  }, []);
  const handleSubmit = async e_1 => {
    e_1.preventDefault();
    const trimmedInput = input.trim();
    if (!trimmedInput || isLoading) return;
    const userMessage = {
      role: 'user',
      content: trimmedInput
    };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);
    try {
      const response = await fetch('/api/chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          messages: [...messages, userMessage],
          sessionId
        })
      });
      if (!response.ok) {
        throw new Error('Failed to get response');
      }
      const data_1 = await response.json();

      // Clear conversation if requested
      if (data_1.clearConversation) {
        clearAiConversation();
        return;
      }
      setMessages(prev_1 => [...prev_1, {
        role: 'assistant',
        content: data_1.message
      }]);
    } catch (error) {
      console.error('Chat error:', error);
      setMessages(prev_0 => [...prev_0, {
        role: 'assistant',
        content: "I'm sorry, I encountered an error. Please try again or contact us at 1.866.663.4483 for assistance."
      }]);
    } finally {
      setIsLoading(false);
    }
  };
  const handleKeyDown = e_2 => {
    if (e_2.key === 'Enter' && !e_2.shiftKey) {
      e_2.preventDefault();
      handleSubmit(e_2);
    }
  };
  const hasItems = panelItems.length > 0;
  const hasVariants = panelItems.some(item => item.type === 'variant');
  const [mounted, setMounted] = useState(false);
  const [productPanelRight, setProductPanelRight] = useState(0);

  // Calculate widget width (mirrors HelpWidget.js logic)
  const getWidgetWidth = () => {
    if (typeof window === 'undefined') return 700;
    const vw = window.innerWidth;
    if (vw >= 1280) return Math.min(vw * 0.4, 700);
    if (vw >= 1024) return Math.min(vw * 0.5, 700);
    if (vw >= 768) return Math.min(vw * 0.7, 700);
    // Below md: fill nearly full width
    return vw - 32; // 16px margin on each side
  };
  useEffect(() => {
    setMounted(true);
    const updatePosition = () => {
      const widgetWidth = getWidgetWidth();
      // Get site padding from CSS variable (convert rem to px)
      const rootFontSize = parseFloat(getComputedStyle(document.documentElement).fontSize);
      const sitePaddingValue = getComputedStyle(document.documentElement).getPropertyValue('--site-padding').trim();
      const sitePadding = parseFloat(sitePaddingValue) * (sitePaddingValue.includes('rem') ? rootFontSize : 1);
      // Widget is at right: site-padding, product panel needs small gap
      setProductPanelRight(widgetWidth + sitePadding + 4);
    };
    updatePosition();
    window.addEventListener('resize', updatePosition);
    return () => window.removeEventListener('resize', updatePosition);
  }, []);

  // Product/variant panel rendered via portal, positioned to the left of the widget
  // Only show when AI assistant is the active panel (close immediately when switching away)
  // Hidden on mobile - products shown inline in messages instead
  const productPanel = hasItems && mounted && currentPanel === 'ai_assistant' ? createPortal(<div className="hidden md:flex fixed bottom-4 w-52 bg-white/95 backdrop-blur-sm border border-gray-10 rounded-lg z-[999] flex-col shadow-xl animate-slide-up" style={{
    right: `${productPanelRight}px`,
    maxHeight: 'calc(100vh - 2rem)',
    height: '600px'
  }}>
            <div className="px-2 py-1.5 border-b border-gray-10 bg-gray-3 rounded-t-lg flex items-center justify-between">
                <span className="text-2xs font-medium text-dark uppercase tracking-wide">
                    {hasVariants ? 'Options' : 'Products'} ({panelItems.length})
                </span>
                <span className="text-3xs text-light">click to view</span>
            </div>
            <div className="flex-1 overflow-y-auto scrollbar scrollbar-y" role="region" aria-label="Product results">
                {panelItems.map((item_0, index) => item_0.type === 'variant' ? <VariantCard key={item_0.data.variant_id || index} variant={item_0.data} /> : <ProductCard key={item_0.data.id || index} product={item_0.data} />)}
            </div>
            <div className="px-2 py-2 border-t border-gray-10 bg-gray-3 rounded-b-lg">
                <button onClick={clearAiConversation} className="w-full px-3 py-1.5 text-2xs text-light hover:text-dark transition-colors">
                    Clear
                </button>
            </div>
        </div>, document.body) : null;
  return <div className="flex flex-col h-full" role="region" aria-label="Shopping Assistant chat">
            {/* Chat Area - Full width */}
            <div className="flex-1 overflow-y-auto scrollbar scrollbar-y" role="log" aria-live="polite">
                <div className="p-3 space-y-2">
                    {messages.map((message_0, index_0) => <Message key={index_0} message={message_0} onMobileLinkClick={minimizeWidget} />)}
                    {isLoading && <LoadingIndicator />}
                    <div ref={messagesEndRef} />
                </div>
            </div>

            {/* Action Bar */}
            {messages.length > 1 && <div className="px-2 py-1.5 border-t border-gray-10 bg-gray-3 flex items-center">
                    <button type="button" onClick={clearAiConversation} className="text-2xs text-light hover:text-dark transition-colors">
                        Clear conversation
                    </button>
                </div>}

            {/* Input Area */}
            <form onSubmit={handleSubmit} className="p-2 border-t border-gray-10 bg-white">
                <label htmlFor="chat-input" className="sr-only">Type your message</label>
                <div className="flex gap-2">
                    <input ref={inputRef} id="chat-input" type="text" value={input} onChange={e_3 => setInput(e_3.target.value)} onKeyDown={handleKeyDown} placeholder={inputPlaceholder} className="flex-1 min-w-0 px-3 py-2 text-xl lg:text-sm border border-gray-10 rounded-sm focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary" disabled={isLoading} />
                    <button type="submit" disabled={isLoading || !input.trim()} className="px-4 py-2 bg-primary text-white text-sm font-medium rounded-sm disabled:opacity-50 shrink-0">
                        Send
                    </button>
                </div>
            </form>

            {/* Product panel rendered outside widget via portal */}
            {productPanel}
        </div>;
};
const LoadingIndicator = () => {
  const $ = _c(7);
  const [seconds, setSeconds] = useState(0);
  let t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = () => {
      const interval = setInterval(() => {
        setSeconds(_temp);
      }, 1000);
      return () => clearInterval(interval);
    };
    t1 = [];
    $[0] = t0;
    $[1] = t1;
  } else {
    t0 = $[0];
    t1 = $[1];
  }
  useEffect(t0, t1);
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <div className="text-2xs font-medium text-primary mb-0.5">Claudio</div>;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <span className="inline-block w-3 h-3 border-2 border-primary border-t-transparent rounded-full animate-spin" />;
    t4 = <span className="text-xs">Thinking...</span>;
    $[3] = t3;
    $[4] = t4;
  } else {
    t3 = $[3];
    t4 = $[4];
  }
  let t5;
  if ($[5] !== seconds) {
    t5 = <div>{t2}<div className="rounded-lg px-3 py-2 bg-gray-3"><div className="flex items-center gap-2 text-sm text-light">{t3}{t4}<span className="text-xs text-extra-light">{seconds}s</span></div></div></div>;
    $[5] = seconds;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  return t5;
};
const Message = t0 => {
  const $ = _c(38);
  const {
    message,
    onMobileLinkClick
  } = t0;
  const isUser = message.role === "user";
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = /```designer\s*\n?([\s\S]*?)```/g;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const designerRegex = t1;
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = /```brand\s*\n?([\s\S]*?)```/g;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const brandRegex = t2;
  let entityData;
  let hasInlineItems;
  let inlineProducts;
  let inlineVariants;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  if ($[2] !== isUser || $[3] !== message.content) {
    t8 = Symbol.for("react.early_return_sentinel");
    bb0: {
      let designerData = null;
      let brandData = null;
      inlineProducts = [];
      inlineVariants = [];
      let match;
      if ((match = designerRegex.exec(message.content)) !== null) {
        ;
        try {
          designerData = JSON.parse(match[1].trim());
        } catch (t9) {
          const e = t9;
        }
      }
      if ((match = brandRegex.exec(message.content)) !== null) {
        ;
        try {
          brandData = JSON.parse(match[1].trim());
        } catch (t9) {
          const e_0 = t9;
        }
      }
      let t9;
      if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
        t9 = /```product\s*\n?([\s\S]*?)```/g;
        $[14] = t9;
      } else {
        t9 = $[14];
      }
      const productRegex2 = t9;
      while ((match = productRegex2.exec(message.content)) !== null) {
        ;
        try {
          inlineProducts.push(JSON.parse(match[1].trim()));
        } catch (t10) {
          const e_1 = t10;
        }
      }
      let t10;
      if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
        t10 = /```variant\s*\n?([\s\S]*?)```/g;
        $[15] = t10;
      } else {
        t10 = $[15];
      }
      const variantRegex2 = t10;
      while ((match = variantRegex2.exec(message.content)) !== null) {
        ;
        try {
          inlineVariants.push(JSON.parse(match[1].trim()));
        } catch (t11) {
          const e_2 = t11;
        }
      }
      const getTextContent = _temp2;
      const textContent = getTextContent(message.content);
      entityData = designerData || brandData;
      designerData ? "designer" : brandData ? "brand" : null;
      hasInlineItems = inlineProducts.length > 0 || inlineVariants.length > 0;
      if (!textContent && !entityData && !hasInlineItems) {
        t8 = null;
        break bb0;
      }
      const t11 = `text-2xs font-medium mb-0.5 ${isUser ? "text-dark" : "text-primary"}`;
      const t12 = isUser ? "You" : "Claudio";
      if ($[16] !== t11 || $[17] !== t12) {
        t7 = <div className={t11}>{t12}</div>;
        $[16] = t11;
        $[17] = t12;
        $[18] = t7;
      } else {
        t7 = $[18];
      }
      t5 = `rounded-lg px-3 py-2 ${isUser ? "bg-gray-5" : "bg-gray-3"}`;
      t6 = entityData && entityData.image && <Link href={toPath(entityData.url)} className="float-left mr-3 mb-1"><Image src={entityData.image} alt={entityData.name || ""} width={80} height={80} className="w-20 h-20 object-cover rounded-sm" /></Link>;
      t3 = "text-sm text-black leading-snug whitespace-pre-line";
      t4 = isUser ? textContent : linkify(textContent);
    }
    $[2] = isUser;
    $[3] = message.content;
    $[4] = entityData;
    $[5] = hasInlineItems;
    $[6] = inlineProducts;
    $[7] = inlineVariants;
    $[8] = t3;
    $[9] = t4;
    $[10] = t5;
    $[11] = t6;
    $[12] = t7;
    $[13] = t8;
  } else {
    entityData = $[4];
    hasInlineItems = $[5];
    inlineProducts = $[6];
    inlineVariants = $[7];
    t3 = $[8];
    t4 = $[9];
    t5 = $[10];
    t6 = $[11];
    t7 = $[12];
    t8 = $[13];
  }
  if (t8 !== Symbol.for("react.early_return_sentinel")) {
    return t8;
  }
  let t9;
  if ($[19] !== t3 || $[20] !== t4) {
    t9 = <div className={t3}>{t4}</div>;
    $[19] = t3;
    $[20] = t4;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  let t10;
  if ($[22] !== entityData) {
    t10 = entityData && <div className="clear-both" />;
    $[22] = entityData;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  let t11;
  if ($[24] !== hasInlineItems || $[25] !== inlineProducts || $[26] !== inlineVariants || $[27] !== onMobileLinkClick) {
    t11 = hasInlineItems && <div className="md:hidden mt-2 pt-2 border-t border-gray-10"><div className="flex flex-wrap gap-1.5">{inlineProducts.map((product, idx) => <InlineProductCard key={product.id || idx} product={product} onClick={onMobileLinkClick} />)}{inlineVariants.map((variant, idx_0) => <InlineVariantCard key={variant.variant_id || idx_0} variant={variant} onClick={onMobileLinkClick} />)}</div></div>;
    $[24] = hasInlineItems;
    $[25] = inlineProducts;
    $[26] = inlineVariants;
    $[27] = onMobileLinkClick;
    $[28] = t11;
  } else {
    t11 = $[28];
  }
  let t12;
  if ($[29] !== t10 || $[30] !== t11 || $[31] !== t5 || $[32] !== t6 || $[33] !== t9) {
    t12 = <div className={t5}>{t6}{t9}{t10}{t11}</div>;
    $[29] = t10;
    $[30] = t11;
    $[31] = t5;
    $[32] = t6;
    $[33] = t9;
    $[34] = t12;
  } else {
    t12 = $[34];
  }
  let t13;
  if ($[35] !== t12 || $[36] !== t7) {
    t13 = <div>{t7}{t12}</div>;
    $[35] = t12;
    $[36] = t7;
    $[37] = t13;
  } else {
    t13 = $[37];
  }
  return t13;
};
const ProductCard = t0 => {
  const $ = _c(25);
  const {
    product
  } = t0;
  const imageUrl = product.image || null;
  let t1;
  if ($[0] !== product.url) {
    t1 = toPath(product.url);
    $[0] = product.url;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== imageUrl || $[3] !== product.name) {
    t2 = imageUrl && <div className="shrink-0 overlay-block"><Image src={imageUrl} alt={product.name || ""} width={48} height={48} className="w-12 h-12 object-contain scale-100 transition-transform duration-300 group-hover:scale-110" /></div>;
    $[2] = imageUrl;
    $[3] = product.name;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== product.name) {
    t3 = <div className="capitalize font-medium text-2xs mb-0.5 line-clamp-2">{product.name}</div>;
    $[5] = product.name;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== product.price) {
    t4 = <div className="capitalize font-light text-3xs text-neutral-600 mb-0.5"><Price amount={product.price} /></div>;
    $[7] = product.price;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== product.brand || $[10] !== product.designer) {
    t5 = product.brand && <span className={`font-medium ${product.designer ? "border-r-1 pr-1 mr-1" : ""}`}>{product.brand}</span>;
    $[9] = product.brand;
    $[10] = product.designer;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== product.designer) {
    t6 = product.designer && <span className="text-light">{product.designer}</span>;
    $[12] = product.designer;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== t5 || $[15] !== t6) {
    t7 = <div className="flex items-center text-3xs">{t5}{t6}</div>;
    $[14] = t5;
    $[15] = t6;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== t3 || $[18] !== t4 || $[19] !== t7) {
    t8 = <div className="flex-1 min-w-0">{t3}{t4}{t7}</div>;
    $[17] = t3;
    $[18] = t4;
    $[19] = t7;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  let t9;
  if ($[21] !== t1 || $[22] !== t2 || $[23] !== t8) {
    t9 = <Link href={t1} className="group flex items-start gap-2 p-2 border-b border-gray-8 last:border-b-0 hover:bg-gray-3 transition-colors">{t2}{t8}</Link>;
    $[21] = t1;
    $[22] = t2;
    $[23] = t8;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  return t9;
};
const VariantCard = t0 => {
  const $ = _c(29);
  const {
    variant
  } = t0;
  const imageUrl = variant.image || null;
  let t1;
  if ($[0] !== variant.url || $[1] !== variant.variant_id) {
    t1 = variant.url ? toPath(`${variant.url}?variant_id=${variant.variant_id}`) : "#";
    $[0] = variant.url;
    $[1] = variant.variant_id;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const variantUrl = t1;
  let t2;
  if ($[3] !== imageUrl || $[4] !== variant.name || $[5] !== variant.option) {
    t2 = <div className="shrink-0 w-12 h-12 flex items-center justify-center">{imageUrl ? <div className="overlay-block"><Image src={imageUrl} alt={variant.option || variant.name || ""} width={48} height={48} className="w-12 h-12 object-contain scale-100 transition-transform duration-300 group-hover:scale-110" /></div> : <FontAwesomeIcon icon={faHiveIconSmallLight} className="w-6 h-6 text-gray-20" />}</div>;
    $[3] = imageUrl;
    $[4] = variant.name;
    $[5] = variant.option;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const t3 = variant.option || variant.name;
  let t4;
  if ($[7] !== t3) {
    t4 = <div className="capitalize font-medium text-2xs mb-0.5 line-clamp-2">{t3}</div>;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== variant.price) {
    t5 = <div className="capitalize font-light text-3xs text-neutral-600 mb-0.5"><Price amount={variant.price} /></div>;
    $[9] = variant.price;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== variant.brand) {
    t6 = variant.brand && <span className="font-medium">{variant.brand}</span>;
    $[11] = variant.brand;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== variant.in_stock) {
    t7 = variant.in_stock === false && <span className="text-extra-light">• Out of stock</span>;
    $[13] = variant.in_stock;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== variant.quick_ship) {
    t8 = variant.quick_ship && <span className="text-green-600">• Quick ship</span>;
    $[15] = variant.quick_ship;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== t6 || $[18] !== t7 || $[19] !== t8) {
    t9 = <div className="flex items-center gap-1 text-3xs">{t6}{t7}{t8}</div>;
    $[17] = t6;
    $[18] = t7;
    $[19] = t8;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  let t10;
  if ($[21] !== t4 || $[22] !== t5 || $[23] !== t9) {
    t10 = <div className="flex-1 min-w-0">{t4}{t5}{t9}</div>;
    $[21] = t4;
    $[22] = t5;
    $[23] = t9;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  let t11;
  if ($[25] !== t10 || $[26] !== t2 || $[27] !== variantUrl) {
    t11 = <Link href={variantUrl} className="group flex items-start gap-2 p-2 border-b border-gray-8 last:border-b-0 hover:bg-gray-3 transition-colors">{t2}{t10}</Link>;
    $[25] = t10;
    $[26] = t2;
    $[27] = variantUrl;
    $[28] = t11;
  } else {
    t11 = $[28];
  }
  return t11;
};

// Compact inline product card for mobile chat display
const InlineProductCard = t0 => {
  const $ = _c(15);
  const {
    product,
    onClick
  } = t0;
  const imageUrl = product.image || null;
  let t1;
  if ($[0] !== product.url) {
    t1 = toPath(product.url);
    $[0] = product.url;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== imageUrl || $[3] !== product.name) {
    t2 = imageUrl && <div className="w-full aspect-square mb-1 overlay-block"><Image src={imageUrl} alt={product.name || ""} width={64} height={64} className="w-full h-full object-contain" /></div>;
    $[2] = imageUrl;
    $[3] = product.name;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== product.name) {
    t3 = <div className="text-3xs font-medium line-clamp-2 leading-tight capitalize">{product.name}</div>;
    $[5] = product.name;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== product.price) {
    t4 = <div className="text-3xs text-light mt-0.5"><Price amount={product.price} /></div>;
    $[7] = product.price;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== onClick || $[10] !== t1 || $[11] !== t2 || $[12] !== t3 || $[13] !== t4) {
    t5 = <Link href={t1} onClick={onClick} className="group flex flex-col w-[72px] p-1 rounded bg-white border border-gray-8 hover:border-gray-15 transition-colors">{t2}{t3}{t4}</Link>;
    $[9] = onClick;
    $[10] = t1;
    $[11] = t2;
    $[12] = t3;
    $[13] = t4;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  return t5;
};

// Compact inline variant card for mobile chat display
const InlineVariantCard = t0 => {
  const $ = _c(17);
  const {
    variant,
    onClick
  } = t0;
  const imageUrl = variant.image || null;
  let t1;
  if ($[0] !== variant.url || $[1] !== variant.variant_id) {
    t1 = variant.url ? toPath(`${variant.url}?variant_id=${variant.variant_id}`) : "#";
    $[0] = variant.url;
    $[1] = variant.variant_id;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const variantUrl = t1;
  let t2;
  if ($[3] !== imageUrl || $[4] !== variant.name || $[5] !== variant.option) {
    t2 = <div className="w-full aspect-square mb-1 flex items-center justify-center">{imageUrl ? <div className="overlay-block w-full h-full"><Image src={imageUrl} alt={variant.option || variant.name || ""} width={64} height={64} className="w-full h-full object-contain" /></div> : <FontAwesomeIcon icon={faHiveIconSmallLight} className="w-6 h-6 text-gray-20" />}</div>;
    $[3] = imageUrl;
    $[4] = variant.name;
    $[5] = variant.option;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const t3 = variant.option || variant.name;
  let t4;
  if ($[7] !== t3) {
    t4 = <div className="text-3xs font-medium line-clamp-2 leading-tight capitalize">{t3}</div>;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== variant.price) {
    t5 = <div className="text-3xs text-light mt-0.5"><Price amount={variant.price} /></div>;
    $[9] = variant.price;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== onClick || $[12] !== t2 || $[13] !== t4 || $[14] !== t5 || $[15] !== variantUrl) {
    t6 = <Link href={variantUrl} onClick={onClick} className="group flex flex-col w-[72px] p-1 rounded bg-white border border-gray-8 hover:border-gray-15 transition-colors">{t2}{t4}{t5}</Link>;
    $[11] = onClick;
    $[12] = t2;
    $[13] = t4;
    $[14] = t5;
    $[15] = variantUrl;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  return t6;
};
export default HelpAIClaudio;
function _temp(s) {
  return s + 1;
}
function _temp2(content) {
  return content.replace(/```product\s*\n?[\s\S]*?```/g, "").replace(/```variant\s*\n?[\s\S]*?```/g, "").replace(/```designer\s*\n?[\s\S]*?```/g, "").replace(/```brand\s*\n?[\s\S]*?```/g, "").replace(/\n{3,}/g, "\n\n").replace(/^\n+|\n+$/g, "").trim();
}